import {
  AttachIncomingDocumentDto,
  BucketValue,
  ChargeDocument,
  CreateOrder,
  FollowUpNamespace,
  HoldNightOwlOrderDto,
  LabAssignThirdPartyDeviceDto,
  LabManualShippingDto,
  LabRequestDeliveryDto,
  LabAssignDeviceAndStartShippingDto,
  Order,
  PatientRelationship,
  Payer,
  AutomatedSms,
  ProvidePaymentAuthorizationDto,
  RefundShippoTransactionDto,
  ReshipThirdPartyDeviceDto,
  RevertNightOwlOrder,
  SendNightOwlWelcomeDto,
  SubmitToNightOwlDto,
  TestCode,
  VerificationResult,
  VerificationSection,
  Message
} from 'store/types'
import { DeviceRemovalReason } from 'store/types'
import { NeededDeviceType } from 'store/types'
import { ChangeFulfillmentType } from 'ts-back-end/src/business/device/dto/device.dto'
import { CreateOrderChargeNight } from 'ts-back-end/src/business/order/dto/create-order-charge.dto'
import type {
  DmeReferralManualDefaults,
  DmeReferralRecipient,
} from 'ts-back-end/src/business/fax-document/types/dme-referral.types'
import {
  TestConditions,
  TimestampOverride,
} from 'ts-back-end/src/business/order/dto/create-order.dto'
import { ShippoTrackResponse } from 'ts-back-end/dist/ts-back-end/src/business/night-owl/types/shippo'
import { GenerateFormData } from 'ts-back-end/dist/ts-back-end/src/business/order/types/generate-form.types';
import type { HstReportRecognition } from 'ts-back-end/src/business/order/types/report.types'
import type { HstCodeTier } from 'ts-back-end/src/business/payer/dto/payer.dto'
import type { OrderHstTieredChargePreview } from 'ts-back-end/src/business/billing-charges/types/hst-tiered-charge.types'
import { baseApi } from './baseApi'
import { AobRequestPayload } from './dmeOrder'

type PhysicianAssignment = {
  physicianId: number
  orderId: number
  addressUuid: string
}

type PayerAssignment = {
  orderId: string
  payers: Payer[]
}

export type SignAobRequest = {
  orderId: number
  signature: string
  relationship: PatientRelationship
  signatureDateTime: string
  signedBy?: string
}

type OrderListRequest = {
  officeIds?: number[]
  types: TestCode
  buckets: BucketValue
  page: number
  size: number
  /** Outsource-visibility filter; only sent on the buckets that offer it (OPEN / DEMO_VER / INS_VER). */
  outsourceVisibility?: 'visible' | 'hidden'
}

// A charge created from an order can spend the patient's prepaid credit at once
// (a self-pay test charge settles from it), so the patient's balances, credit
// and charges are stale too. Only the order ids are known here, hence every
// patient's tag.
const CHARGE_CREATED_TAGS = ['Patient', 'BillingCharges', 'BillingPatientPayments'] as const

export const orderApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLabOrder: builder.query<Order, number>({
      query: (id) => ({ url: `/lab/orders/${id}` }),
      providesTags: (result, error, id) => [{ type: 'Order', id }],
      keepUnusedDataFor: 5,
    }),
    getLabOrders: builder.query<Order[], OrderListRequest>({
      query: (query) => ({ url: `/lab/orders`, params: query }),
      providesTags: ['OrderList'],
    }),
    getRecentLabOrders: builder.query<Order[], OrderListRequest>({
      query: (query) => ({ url: `/lab/orders/recent`, params: query }),
      providesTags: ['OrderList'],
    }),
    setNeededDeviceType: builder.mutation<Order, { orderId: number; neededDeviceType: NeededDeviceType | null }>({
      query: ({ orderId, neededDeviceType }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/needed-device-type`,
        body: { neededDeviceType },
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderList' },
      ],
    }),
    startOrderVerification: builder.mutation<Order, { orderId: string }>({
      query: ({ orderId }) => ({
        method: `PUT`,
        url: `/lab/orders/${orderId}/verification`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    finishOrderVerification: builder.mutation<Order, { orderId: string }>({
      query: ({ orderId }) => ({
        method: `PATCH`,
        url: `/lab/orders/${orderId}/verification`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    skipLabOfficeNotesUpload: builder.mutation<Order, { orderId: number }>({
      query: ({ orderId }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/office-notes/skip`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    addOrderDiagnosisCode: builder.mutation<Order, { orderId: string; code: string }>({
      query: ({ orderId, code }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/diagnosis-codes`,
        body: { orderId, code },
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    removeOrderDiagnosisCode: builder.mutation<Order, { orderId: string; code: string }>({
      query: ({ orderId, code }) => ({
        method: `DELETE`,
        url: `/lab/orders/${orderId}/diagnosis-codes/${code}`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    updateLabOrderPayer: builder.mutation<Payer, PayerAssignment>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/payers`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderList' },
      ],
    }),
    setLabOrderPayerInfo: builder.mutation<Payer, ProvidePaymentAuthorizationDto>({
      query: ({ orderId, ...payerInfo }) => ({
        method: `PUT`,
        url: `/lab/orders/${orderId}/authorization`,
        body: { orderId, ...payerInfo },
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderList' },
        { type: 'OrderCounts' },
      ],
    }),
    addLabFile: builder.mutation<{ result: string; message?: string }, { orderId: number; uploadType: string; fileKey: string }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/files`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    recordLabDocumentDownload: builder.mutation<void, { orderId: number; fileKey: string }>({
      query: ({ orderId, ...body }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/files/download-activity`,
        body,
      }),
    }),
    createLabPaymentRedirect: builder.mutation<{ url: string }, { orderId: number }>({
      query: ({ orderId }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/payment-redirect`,
      }),
    }),
    resendPaymentLink: builder.mutation<void, { orderId: number }>({
      query: ({ orderId }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/resend-payment-link`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }, 'Messages'],
    }),
    removeLabFile: builder.mutation<Order, { orderId: number; fileKey: string }>({
      query: (params) => ({
        method: `DELETE`,
        url: `/lab/orders/${params.orderId}/files`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    setLabReportPending: builder.mutation<void, { orderId: number; pending: boolean }>({
      query: ({ orderId, pending }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/report-pending`,
        body: { pending },
      }),
      invalidatesTags: (result, error, { orderId }) => [{ type: 'Order', id: orderId }],
    }),
    attachIncomingDocument: builder.mutation<void, AttachIncomingDocumentDto>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/files`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'IncomingDocument' },
      ],
    }),
    changeLabFileCategory: builder.mutation<
      Order,
      { orderId: number; fileKey: string; toUploadType: string }
    >({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/files/change-category`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    // Takes an Rx candidate as the order's RX; the current RX becomes a
    // candidate and its verification, if any, is sent back to the lab.
    acceptLabRxCandidate: builder.mutation<Order, { orderId: number; fileKey: string }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/files/accept-rx-candidate`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    sendFax: builder.mutation<
      void,
      { orderId: number; documentKeys: string[]; message: string; faxNumber: string }
    >({
      query: ({ orderId, ...body }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/fax`,
        body: { orderId, ...body },
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }, { type: 'OrderList' }],
    }),
    cancelFileFax: builder.mutation<void, { orderId: number; documentKeys: string[] }>({
      query: ({ orderId, documentKeys }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/cancel-file-faxing`,
        body: { orderId, documentKeys },
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }, { type: 'OrderList' }],
    }),
    // The DME referral packet sent by hand (TES-3159).
    getDmeReferralPacketDefaults: builder.query<DmeReferralManualDefaults, { orderId: number }>({
      query: ({ orderId }) => ({ url: `/lab/orders/${orderId}/dme-referral-packet` }),
      keepUnusedDataFor: 0,
    }),
    sendDmeReferralPacket: builder.mutation<
      DmeReferralRecipient,
      { orderId: number; signedRxFileKey: string; dmeOfficeId: number }
    >({
      query: ({ orderId, ...body }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/dme-referral-packet`,
        body,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }, { type: 'OrderList' }],
    }),
    verifyOrder: builder.mutation<Order, VerificationResult>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/verify`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    unverifyOrder: builder.mutation<Order, { orderId: number; section: VerificationSection }>({
      query: ({ orderId, section }) => ({
        method: `DELETE`,
        url: `/lab/orders/${orderId}/verify/${section}`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    revokeVerification: builder.mutation<Order, { orderId: number }>({
      query: ({ orderId }) => ({
        method: `DELETE`,
        url: `/lab/orders/${orderId}/verification`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    assignLabPhysician: builder.mutation<Order, PhysicianAssignment>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/physician`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    removeLabPhysician: builder.mutation<Order, { orderId: number }>({
      query: ({ orderId }) => ({
        method: `DELETE`,
        url: `/lab/orders/${orderId}/physician`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderList' },
      ],
    }),
    getLabOrderCounts: builder.query<any[], {}>({
      query: (query) => ({ url: `/lab/orders/counts` }),
      providesTags: ['OrderCounts'],
    }),
    searchOrders: builder.query<
      Order[],
      { buckets: BucketValue[]; patient: string; page: number; size: number }
    >({
      query: (params) => ({
        url: `/lab/orders/search`,
        params: {
          ...params,
          buckets: params?.buckets?.join(','),
        },
      }),
    }),
    putNightOwlOnHold: builder.mutation<Order, HoldNightOwlOrderDto>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/night-owl/hold`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    removeNightOwlHold: builder.mutation<Order, { orderId: number }>({
      query: (params) => ({
        method: `DELETE`,
        url: `/lab/orders/${params.orderId}/night-owl/hold`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    submitToNightOwl: builder.mutation<Order, SubmitToNightOwlDto>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/night-owl/submit`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    sendNightOwlWelcome: builder.mutation<Order, SendNightOwlWelcomeDto>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/night-owl/welcome`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    assignTpdDeviceAndStartShipping: builder.mutation<Order, LabAssignDeviceAndStartShippingDto>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/tpd/assign-device-and-start-shipping`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
        { type: 'Devices' },
      ],
    }),
    assignNightOwlDevice: builder.mutation<Order, LabAssignThirdPartyDeviceDto>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/night-owl/assign-device`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
        { type: 'Devices' },
      ],
    }),
    cancelWesperOrder: builder.mutation<
      { success: boolean; error?: string },
      { orderId: number; reason?: string }
    >({
      query: ({ orderId, reason }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/wesper/cancel`,
        body: { reason },
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    createWesperOrder: builder.mutation<
      { success: boolean; error?: string },
      { orderId: number; fulfillment: 'DISPENSE' | 'DROPSHIP' }
    >({
      query: ({ orderId, fulfillment }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/wesper/create`,
        body: { fulfillment },
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    setInterpretingPhysician: builder.mutation<
      { success: boolean },
      { orderId: number; physicianId: number | null }
    >({
      query: ({ orderId, physicianId }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/interpreting-physician`,
        body: { physicianId },
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    initiateTestSmarterFulfillment: builder.mutation<Order, { orderId: number }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/initiate-ts-fulfillment`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    getShippingRates: builder.mutation<Order, { orderId: number; max_price?: number }>({
      query: ({ orderId, ...query }) => ({
        method: `GET`,
        url: `/lab/orders/${orderId}/night-owl/rates`,
        params: query,
      }),
    }),
    validateShippingAddress: builder.mutation<Order, { orderId: number }>({
      query: ({ orderId, ...query }) => ({
        method: `GET`,
        url: `/lab/orders/${orderId}/tpd/validate-address`,
        params: query,
      }),
    }),
    requestDelivery: builder.mutation<Order, LabRequestDeliveryDto>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/night-owl/request-delivery`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    initiateManualShipping: builder.mutation<Order, LabManualShippingDto>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/night-owl/manual-shipping`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    localPickup: builder.mutation<Order, { orderId: number }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/night-owl/local-pickup`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    refundNightOwlShipment: builder.mutation<Order, RefundShippoTransactionDto>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/night-owl/refund`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    revertNightOwl: builder.mutation<Order, RevertNightOwlOrder>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/night-owl/revert`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    returnThirdPartyDevice: builder.mutation<Order, RevertNightOwlOrder>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/tpd/mark-as-returned`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
        { type: 'Devices' },
      ],
    }),
    reshipThirdPartyDevice: builder.mutation<Order, ReshipThirdPartyDeviceDto>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/tpd/reship`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    changeNightOwlFulfillment: builder.mutation<Order, ChangeFulfillmentType>({
      query: (params) => ({
        method: `PATCH`,
        url: `/lab/orders/${params.orderId}/night-owl/fulfillment`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderList' },
      ],
    }),
    markNightOwlDelivered: builder.mutation<Order, { orderId: number }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/night-owl/delivered`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    deviceLostReversion: builder.mutation<Order, { orderId: number; reason: DeviceRemovalReason }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/night-owl/device-lost-reversion`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
        { type: 'Notes' },
        { type: 'Devices' },
      ],
    }),
    getNightOwlShippingHistory: builder.query<ShippoTrackResponse, { orderId: number; isReturn?: boolean }>({
      query: ({ orderId, isReturn = false }) => ({
        url: `/lab/orders/${orderId}/shippo/tracking?fakeResponse=false&debug=false&isReturn=${isReturn}`,
      }),
    }),
    labAobSignRequest: builder.mutation<Order, AobRequestPayload>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/aob-request`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    addToBillingQueue: builder.mutation<Order, { orders: number[] }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/billing/clear`,
        body: params,
      }),
      invalidatesTags: (result, error, { orders }) => [
        ...orders.map((id) => ({ type: 'Order', id })),
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    createChargeFromOrder: builder.mutation<void, { orderId: number }>({
      query: ({ orderId }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/charges/create`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    createOrderCharge: builder.mutation<
      void,
      {
        orderId: number
        nights: CreateOrderChargeNight[]
        submitDocuments?: boolean
        documentKeys?: string[]
        hstTier?: HstCodeTier
      }
    >({
      query: ({ orderId, nights, submitDocuments, documentKeys, hstTier }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/charge`,
        body: { nights, submitDocuments, documentKeys, hstTier },
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }, ...CHARGE_CREATED_TAGS],
    }),
    getOrderHstTieredCharge: builder.query<
      OrderHstTieredChargePreview,
      { orderId: number; tier?: HstCodeTier }
    >({
      query: ({ orderId, tier }) => ({
        url: `/lab/orders/${orderId}/charge/hst-tiered`,
        params: tier ? { tier } : undefined,
      }),
    }),
    getOrderChargeDocuments: builder.query<ChargeDocument[], { orderId: number }>({
      query: ({ orderId }) => ({ url: `/lab/orders/${orderId}/charge/documents` }),
    }),
    createCharges: builder.mutation<Order, { orders: number[] }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/billing/charges`,
        body: params,
      }),
      invalidatesTags: (result, error, { orders }) => [
        ...orders.map((id) => ({ type: 'Order', id })),
        { type: 'OrderCounts' },
        { type: 'OrderList' },
        ...CHARGE_CREATED_TAGS,
      ],
    }),
    removeFromBillingQueue: builder.mutation<Order, { orders: number[] }>({
      query: (params) => ({
        method: `DELETE`,
        url: `/lab/orders/billing/clear`,
        body: params,
      }),
      invalidatesTags: (result, error, { orders }) => [
        ...orders.map((id) => ({ type: 'Order', id })),
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    createLabOrder: builder.mutation<Order, CreateOrder>({
      query: (order) => ({
        method: `POST`,
        url: `/lab/orders`,
        body: order,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    cancelReportFax: builder.mutation<Order, { orderId: number }>({
      query: (params) => ({
        method: `DELETE`,
        url: `/lab/orders/${params.orderId}/fax-schedule`,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    scheduleReportFax: builder.mutation<Order, { orderId: number; in: number }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/fax-schedule`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    capturePatientConsent: builder.mutation<Order, { orderId: number; result: string }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/check-patient-end`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    sendPatientPaperworkEmail: builder.mutation<
      Order,
      { orderId: number; email: string; method: 'email' | 'sms'; test?: boolean }
    >({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/send-paperwork-link`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    bypassStripePayment: builder.mutation<Order, { orderId: number }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/bypass-payment`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    initiateManualQuestionnaire: builder.mutation<Order, { orderId: number }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/manual-questionnaire`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    setCopayAmount: builder.mutation<Order, { orderId: number; value: number | null }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/copay`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    createCheckout: builder.mutation<{ url: string | null }, { orderId: number; amount: number }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/checkout`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    setOrderFollowUpDate: builder.mutation<
      Order,
      {
        orderId: number
        date: string
        namespace: FollowUpNamespace
        comment: string
        humanFollowUpRequired?: boolean
        humanFollowUpReason?: string | null
      }
    >({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/follow-ups`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderList' },
      ],
    }),
    updateLabHstDevice: builder.mutation<
      Order,
      { orderId: number; deviceType: string; email: string; mobilePhone: string }
    >({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/hst-device`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    updateHstStatus: builder.mutation<Order, { orderId: number; status: string }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/update-status`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    updateShippingStatus: builder.mutation<Order, { orderId: number; status: string }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/tpd/update-shipping-status`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    cancelOrder: builder.mutation<
      Order,
      {
        orderId: number
        reason: string
        description?: string
        disqualifyingConditions?: string[]
      }
    >({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/cancel`,
        body: {
          reason: params.reason,
          description: params.description,
          disqualifyingConditions: params.disqualifyingConditions,
        },
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderList' },
        { type: 'OrderCounts' },
      ],
    }),
    restoreOrder: builder.mutation<Order, { orderId: number }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/restore`,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderList' },
        { type: 'OrderCounts' },
      ],
    }),
    updateTask: builder.mutation<
      Order,
      {
        orderId: number
        id: string
        resolved: boolean
        timestampOverrides: TimestampOverride[]
      }
    >({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/tasks/${params.id}`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    updateReportRender: builder.mutation<
      Order,
      {
        orderId: number
        timestampOverrides?: TimestampOverride[]
        sessions?: number[]
        tests?: TestConditions[]
      }
    >({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/request-new-render`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [{ type: 'Order', id }],
    }),
    reRenderReport: builder.mutation<Order, { orderId: number; reportId: number }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/reports/${params.reportId}/re-render`,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    removeDeviceReportData: builder.mutation<Order, { orderId: number }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/wipe`,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    getOrderHistoryLog: builder.query<any[], { orderId: number; context: 'LAB' | 'DME' }>({
      query: ({ orderId, context }) => ({
        url: `/${context.toLowerCase()}/orders/${orderId}/history`,
      }),
    }),
    moveDeviceData: builder.mutation<Order, { orderId: number; targetOrderId: number }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/move`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    markOrderReportAsSent: builder.mutation<Order, { orderId: number; reports: string[] }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/mark-as-sent`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    refaxOrderReport: builder.mutation<Order, { orderId: number; reports: string[] }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/re-fax`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    cancelOrderFax: builder.mutation<Order, { orderId: number; reports: string[] }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/cancel-faxing`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    disregardOrderFax: builder.mutation<
      Order,
      { orderId: number; reports: number[]; sendFax: boolean; toFax: string }
    >({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/disregard-reports`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    submitBilling: builder.mutation<Order, { orders: number[] }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/run-billing-queue`,
        body: params,
      }),
      invalidatesTags: (result, error, { orders }) => [
        ...orders.map((id) => ({ type: 'Order', id })),
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    createMobileInvite: builder.mutation<
      Order,
      { orderId: number; method: string; email: string; phone?: string }
    >({
      query: (params) => ({
        method: `POST`,
        url: `/patient-mobile-app/invite`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    markOrderAsBilled: builder.mutation<Order, { orderId: number; reason: string }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/${params.orderId}/mark-billed`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    markOrdersAsBilled: builder.mutation<{ success: boolean }, { orders: number[]; reason?: string }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/orders/mark-billed`,
        body: params,
      }),
      invalidatesTags: (result, error, { orders }) => [
        ...orders.map((id) => ({ type: 'Order', id })),
        { type: 'OrderCounts' },
        { type: 'OrderList' },
      ],
    }),
    // Reads the Upload HST Report form's fields off the report PDF; stores nothing.
    recognizeHstReport: builder.mutation<HstReportRecognition, { orderId: number; formData: FormData }>({
      query: ({ orderId, formData }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/recognize-report`,
        body: formData,
        prepareHeaders: (headers: any) => {
          headers.set('Content-Type', 'multipart/form-data')
          return headers
        },
      }),
      // The scan is stored on the order, so the next open of the modal already has it.
      invalidatesTags: ['Order'],
    }),
    uploadHstReport: builder.mutation<Order, { orderId: number; formData: FormData }>({
      query: ({ orderId, formData }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/upload-report`,
        body: formData,
        prepareHeaders: (headers: any) => {
          headers.set('Content-Type', 'multipart/form-data')
          return headers
        },
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    attachWesperReport: builder.mutation<
      { success: boolean },
      {
        orderId: number
        body: {
          sessionId: string
          datesOfService: string
          ahi?: string
          interpretingPhysicianNpi?: string
          interpretingDate?: string
          includePapScript?: boolean
          includeDmeInfoInPap?: boolean
          apapMin?: string
          apapMax?: string
          followUpCarePhysicianId?: number
          autoFaxFollowUpCare?: boolean
        }
      }
    >({
      query: ({ orderId, body }) => ({
        method: 'POST',
        url: `/lab/orders/${orderId}/wesper/attach-report`,
        body,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    provideFollowUpCareProvider: builder.mutation<
      { success: boolean },
      { orderId: number; physicianId: number }
    >({
      query: ({ orderId, physicianId }) => ({
        method: 'POST',
        url: `/lab/orders/${orderId}/follow-up-care/provider`,
        body: { orderId, physicianId },
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: 'Order', id: orderId },
        'OrderList',
        'OrderCounts',
      ],
    }),
    updateFollowUpCareStatus: builder.mutation<
      { success: boolean },
      { orderId: number; status: string }
    >({
      query: ({ orderId, status }) => ({
        method: 'PATCH',
        url: `/lab/orders/${orderId}/follow-up-care/status`,
        body: { orderId, status },
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: 'Order', id: orderId },
        'OrderList',
        'OrderCounts',
      ],
    }),
    updateFollowUpCarePhysicianStatus: builder.mutation<
      { success: boolean },
      { orderId: number; status: string | null }
    >({
      query: ({ orderId, status }) => ({
        method: 'PATCH',
        url: `/lab/orders/${orderId}/follow-up-care/physician-status`,
        body: { orderId, status },
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: 'Order', id: orderId },
        'OrderList',
        'OrderCounts',
      ],
    }),
    updateFollowUpCareScheduledDate: builder.mutation<
      { success: boolean },
      { orderId: number; scheduledDate: string | null }
    >({
      query: ({ orderId, scheduledDate }) => ({
        method: 'PATCH',
        url: `/lab/orders/${orderId}/follow-up-care/scheduled-date`,
        body: { orderId, scheduledDate },
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: 'Order', id: orderId },
        'OrderList',
        'OrderCounts',
      ],
    }),
    markFollowUpCareReferralLetterSent: builder.mutation<
      { success: boolean },
      { orderId: number; sent: boolean }
    >({
      query: ({ orderId, sent }) => ({
        method: 'PATCH',
        url: `/lab/orders/${orderId}/follow-up-care/referral-letter-sent`,
        body: { orderId, sent },
      }),
      invalidatesTags: (result, error, { orderId }) => [
        { type: 'Order', id: orderId },
        'OrderList',
        'OrderCounts',
      ],
    }),
    completeHstOrder: builder.mutation<Order, { orderId: number }>({
      query: ({ orderId }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/complete-hst`,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    reopenHstPsgOrder: builder.mutation<Order, { orderId: number }>({
      query: ({ orderId }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/reopen-hst-psg`,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    hstDeviceReversion: builder.mutation<
      { success: boolean; order: Order; message: string },
      { orderId: number; reason: DeviceRemovalReason }
    >({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/hst-device-reversion`,
        body: params,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderCounts' },
        { type: 'OrderList' },
        { type: 'Notes' },
        { type: 'Devices' },
      ],
    }),
    scheduleInLabTesting: builder.mutation<Order, { orderId: number; date: string | undefined }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/psg/${params.orderId}/schedule-testing`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    changeInLabStep: builder.mutation<Order, { orderId: number; type: 'prev' | 'next' }>({
      query: (params) => ({
        method: `PUT`,
        url: `/lab/psg/${params.orderId}/state-step`,
        body: params,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    sendPatientDataToCureMd: builder.mutation<Order, { orderId: number }>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/psg/${params.orderId}/send-patient-cure-md`,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    setOrderPriorityLevel: builder.mutation<Order, { orderId: number; priorityLevel: number }>({
      query: ({ orderId, priorityLevel }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/priority-level`,
        body: { priorityLevel },
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    // The biller's answer to a Mixed office's Client Bill vs Insurance review.
    confirmOrderBillingType: builder.mutation<void, { orderId: number; billingType: 'INSURANCE' | 'CLIENT_BILL' }>({
      query: ({ orderId, billingType }) => ({
        method: `PUT`,
        url: `/lab/orders/${orderId}/billing-type`,
        body: { billingType },
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    // Turns a client-billed order back into an insurance one (payer-edit rights).
    unlockOrderClientBilling: builder.mutation<void, { orderId: number }>({
      query: ({ orderId }) => ({
        method: `PUT`,
        url: `/lab/orders/${orderId}/billing-type/unlock`,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    requestInsuranceVerification: builder.mutation<Order, { orderId: number }>({
      query: ({ orderId }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/request-insurance-verification`,
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    reviewInsuranceVerification: builder.mutation<
      { success: boolean },
      { orderId: number; insuranceVerificationId: string; status: 'APPROVED' | 'FAILED'; reason?: string }
    >({
      query: ({ orderId, insuranceVerificationId, ...body }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/insurance-verification/${insuranceVerificationId}/review`,
        body,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderList' },
        { type: 'OrderCounts' },
      ],
    }),
    updateInsuranceVerificationFlags: builder.mutation<
      { result: string },
      { orderId: number; insuranceVerificationId: string; isMedicareAdvantage?: boolean; isMedicaid?: boolean }
    >({
      query: ({ orderId, insuranceVerificationId, ...body }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/insurance-verification/${insuranceVerificationId}/flags`,
        body,
      }),
      invalidatesTags: (result, error, { orderId: id }) => [
        { type: 'Order', id },
        { type: 'OrderList' },
        { type: 'OrderCounts' },
      ],
    }),
    sendSmsToPatient: builder.mutation<{ success: boolean; messageId?: string }, { orderId: number; message: string }>({
      query: ({ orderId, message }) => ({
        method: `POST`,
        url: `/lab/orders/${orderId}/send-sms`,
        body: { message },
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts', 'Messages'],
    }),
    updateSmsIgnoreTimezone: builder.mutation<{ success: boolean }, { orderId: number; ignoreTimezone: boolean }>({
      query: ({ orderId, ignoreTimezone }) => ({
        method: 'PATCH',
        url: `/lab/orders/${orderId}/sms-ignore-timezone`,
        body: { ignoreTimezone },
      }),
      invalidatesTags: ['Order', 'OrderList', 'OrderCounts'],
    }),
    getSmsMessages: builder.query<Message[], number>({
      query: (orderId) => ({ url: `/lab/orders/${orderId}/sms-messages` }),
    }),
    getAutomatedSmsMessages: builder.query<AutomatedSms[], number>({
      query: (orderId) => ({ url: `/lab/orders/${orderId}/automated-sms` }),
    }),
    generateForm: builder.mutation<Order, GenerateFormData>({
      query: (params) => ({
        method: `POST`,
        url: `/lab/orders/${params.orderId}/generate/form`,
        body: params,
      }),
      invalidatesTags: ['Order'],
    }),
  }),
})

export const {
  useGetLabOrdersQuery,
  useGetLabOrderQuery,
  useLazyGetLabOrderQuery,
  useGetRecentLabOrdersQuery,
  useStartOrderVerificationMutation,
  useFinishOrderVerificationMutation,
  useSkipLabOfficeNotesUploadMutation,
  useAddOrderDiagnosisCodeMutation,
  useRemoveOrderDiagnosisCodeMutation,
  useGetLabOrderCountsQuery,
  useUpdateLabOrderPayerMutation,
  useSetLabOrderPayerInfoMutation,
  useAddLabFileMutation,
  useRecordLabDocumentDownloadMutation,
  useRemoveLabFileMutation,
  useAttachIncomingDocumentMutation,
  useChangeLabFileCategoryMutation,
  useAcceptLabRxCandidateMutation,
  useVerifyOrderMutation,
  useUnverifyOrderMutation,
  useRevokeVerificationMutation,
  useAssignLabPhysicianMutation,
  useRemoveLabPhysicianMutation,
  useSearchOrdersQuery,
  usePutNightOwlOnHoldMutation,
  useRemoveNightOwlHoldMutation,
  useSubmitToNightOwlMutation,
  useSendNightOwlWelcomeMutation,
  useAssignTpdDeviceAndStartShippingMutation,
  useAssignNightOwlDeviceMutation,
  useCancelWesperOrderMutation,
  useCreateWesperOrderMutation,
  useSetInterpretingPhysicianMutation,
  useInitiateTestSmarterFulfillmentMutation,
  useGetShippingRatesMutation,
  useValidateShippingAddressMutation,
  useRequestDeliveryMutation,
  useInitiateManualShippingMutation,
  useLocalPickupMutation,
  useRefundNightOwlShipmentMutation,
  useRevertNightOwlMutation,
  useReturnThirdPartyDeviceMutation,
  useReshipThirdPartyDeviceMutation,
  useMarkNightOwlDeliveredMutation,
  useDeviceLostReversionMutation,
  useLazyGetNightOwlShippingHistoryQuery,
  useLabAobSignRequestMutation,
  useCreateLabPaymentRedirectMutation,
  useResendPaymentLinkMutation,
  useChangeNightOwlFulfillmentMutation,
  useCreateChargesMutation,
  useCreateChargeFromOrderMutation,
  useCreateOrderChargeMutation,
  useGetOrderChargeDocumentsQuery,
  useGetOrderHstTieredChargeQuery,
  useAddToBillingQueueMutation,
  useRemoveFromBillingQueueMutation,
  useCreateLabOrderMutation,
  useCancelReportFaxMutation,
  useScheduleReportFaxMutation,
  useCapturePatientConsentMutation,
  useSendPatientPaperworkEmailMutation,
  useBypassStripePaymentMutation,
  useInitiateManualQuestionnaireMutation,
  useSetCopayAmountMutation,
  useCreateCheckoutMutation,
  useSetOrderFollowUpDateMutation,
  useUpdateLabHstDeviceMutation,
  useUpdateHstStatusMutation,
  useUpdateShippingStatusMutation,
  useCancelOrderMutation,
  useRestoreOrderMutation,
  useUpdateTaskMutation,
  useUpdateReportRenderMutation,
  useReRenderReportMutation,
  useRemoveDeviceReportDataMutation,
  useGetOrderHistoryLogQuery,
  useLazyGetOrderHistoryLogQuery,
  useMoveDeviceDataMutation,
  useMarkOrderReportAsSentMutation,
  useRefaxOrderReportMutation,
  useCancelOrderFaxMutation,
  useDisregardOrderFaxMutation,
  useSubmitBillingMutation,
  useCreateMobileInviteMutation,
  useMarkOrderAsBilledMutation,
  useMarkOrdersAsBilledMutation,
  useUploadHstReportMutation,
  useRecognizeHstReportMutation,
  useAttachWesperReportMutation,
  useProvideFollowUpCareProviderMutation,
  useUpdateFollowUpCareStatusMutation,
  useUpdateFollowUpCarePhysicianStatusMutation,
  useUpdateFollowUpCareScheduledDateMutation,
  useMarkFollowUpCareReferralLetterSentMutation,
  useCompleteHstOrderMutation,
  useReopenHstPsgOrderMutation,
  useHstDeviceReversionMutation,
  useScheduleInLabTestingMutation,
  useChangeInLabStepMutation,
  useSendPatientDataToCureMdMutation,
  useSetOrderPriorityLevelMutation,
  useConfirmOrderBillingTypeMutation,
  useUnlockOrderClientBillingMutation,
  useSetNeededDeviceTypeMutation,
  useRequestInsuranceVerificationMutation,
  useReviewInsuranceVerificationMutation,
  useUpdateInsuranceVerificationFlagsMutation,
  useSendSmsToPatientMutation,
  useUpdateSmsIgnoreTimezoneMutation,
  useGetSmsMessagesQuery,
  useLazyGetSmsMessagesQuery,
  useGetAutomatedSmsMessagesQuery,
  useGenerateFormMutation,
  useSendFaxMutation,
  useCancelFileFaxMutation,
  useGetDmeReferralPacketDefaultsQuery,
  useSendDmeReferralPacketMutation,
  useSetLabReportPendingMutation,
} = orderApi
